import { createContext, useContext, useMemo, useState } from "react";
import { toast } from "sonner";
import { motion, AnimatePresence } from "framer-motion";
import { X, Send } from "lucide-react";
import { GoldButton } from "./Brand";
import { COURSE_CATEGORIES, CONTACT } from "../lib/data";
import { submitEnquiry } from "../lib/submitEnquiry";

const EnquiryContext = createContext(null);
export const useEnquiry = () => useContext(EnquiryContext);

const empty = {
  name: "",
  email: "",
  phone: "",
  category: "",
  course: "",
  preferred_contact: "",
  message: "",
};

const CONTACT_METHODS = ["Email", "Phone", "WhatsApp"];

export const EnquiryProvider = ({ children }) => {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);

  const openEnquiry = (course = "") => {
    setForm({ ...empty, course });
    setOpen(true);
  };

  const change = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const changeCategory = (e) => setForm((f) => ({ ...f, category: e.target.value, course: "" }));

  const activeCategory = useMemo(
    () => COURSE_CATEGORIES.find((c) => c.name === form.category),
    [form.category]
  );

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await submitEnquiry({ ...form, source: "enquiry-dialog" });
      toast.success("Thank you, your enquiry has been sent. We'll be in touch shortly.");
      setOpen(false);
      setForm(empty);
    } catch (err) {
      if (err && err.code === "no-php") {
        toast.error(`Contact form works only on the live site. In the meantime, please email ${CONTACT.email}.`);
      } else if (err && err.code === "rate-limit") {
        toast.error("Too many submissions from this network. Please try again later.");
      } else {
        toast.error(`Something went wrong. Please try again or email ${CONTACT.email}.`);
      }
    } finally {
      setLoading(false);
    }
  };

  const field = "w-full bg-transparent border-b border-white/20 focus:border-gold text-white placeholder:text-white/40 py-3 px-1 outline-none transition-colors duration-300 font-manrope";

  return (
    <EnquiryContext.Provider value={{ openEnquiry }}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-6"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div
              className="absolute inset-0 bg-navy/80 backdrop-blur-md"
              onClick={() => setOpen(false)}
              data-testid="enquiry-overlay"
            />
            <motion.div
              className="relative w-full max-w-lg bg-navy-surface border border-gold/25 p-8 md:p-12 max-h-[92vh] overflow-y-auto"
              initial={{ y: 40, opacity: 0, scale: 0.98 }}
              animate={{ y: 0, opacity: 1, scale: 1 }}
              exit={{ y: 20, opacity: 0, scale: 0.98 }}
              transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
              data-testid="enquiry-dialog"
            >
              <button
                onClick={() => setOpen(false)}
                className="absolute top-5 right-5 text-white/50 hover:text-gold transition-colors"
                data-testid="enquiry-close-button"
                aria-label="Close"
              >
                <X strokeWidth={1.2} size={24} />
              </button>
              <p className="text-xs tracking-[0.25em] uppercase text-gold mb-3">Enquire Now</p>
              <h3 className="font-playfair text-3xl md:text-4xl text-white mb-8 leading-tight">
                Start your<br />aesthetics journey
              </h3>
              <form onSubmit={submit} className="space-y-6">
                {/* Honeypot: hidden from real users, if filled we silently drop */}
                <input
                  type="text"
                  name="company"
                  tabIndex={-1}
                  autoComplete="off"
                  className="hidden"
                  aria-hidden="true"
                />
                <input data-testid="enquiry-name-input"  className={field} placeholder="Full name *"   value={form.name}  onChange={change("name")} />
                <input data-testid="enquiry-email-input" className={field} placeholder="Email address *" type="email" value={form.email} onChange={change("email")} />
                <input data-testid="enquiry-phone-input" className={field} placeholder="Phone number"    value={form.phone} onChange={change("phone")} />
                <select
                  data-testid="enquiry-category-select"
                  className={`${field} appearance-none cursor-pointer text-white/70`}
                  value={form.category}
                  onChange={changeCategory}
                >
                  <option value="" className="bg-navy-surface">Course of interest</option>
                  {COURSE_CATEGORIES.map((c) => (
                    <option key={c.id} value={c.name} className="bg-navy-surface">{c.name}</option>
                  ))}
                </select>
                {activeCategory && activeCategory.options.length > 0 && (
                  <select
                    data-testid="enquiry-course-select"
                    className={`${field} appearance-none cursor-pointer text-white/70`}
                    value={form.course}
                    onChange={change("course")}
                  >
                    <option value="" className="bg-navy-surface">Select a specific course (optional)</option>
                    {activeCategory.options.map((c) => (
                      <option key={c.id} value={c.name} className="bg-navy-surface">{c.name}</option>
                    ))}
                  </select>
                )}
                <div>
                  <p className="text-[11px] tracking-[0.2em] uppercase text-white/50 mb-3">Preferred Contact Method</p>
                  <div className="flex flex-wrap gap-2">
                    {CONTACT_METHODS.map((m) => {
                      const active = form.preferred_contact === m;
                      return (
                        <button
                          key={m}
                          type="button"
                          data-testid={`enquiry-prefer-${m.toLowerCase()}`}
                          onClick={() => setForm((f) => ({ ...f, preferred_contact: active ? "" : m }))}
                          className={`px-4 py-2 text-[11px] tracking-[0.15em] uppercase border transition-colors ${
                            active
                              ? "border-gold bg-gold/10 text-gold"
                              : "border-white/20 text-white/70 hover:border-gold/60 hover:text-gold"
                          }`}
                        >
                          {m}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <textarea data-testid="enquiry-message-input" rows={3} className={`${field} resize-none`} placeholder="Your message *" value={form.message} onChange={change("message")} />
                <GoldButton type="submit" disabled={loading} data-testid="enquiry-submit-button" className="w-full disabled:opacity-60">
                  {loading ? "Sending..." : (<>Send Enquiry <Send size={16} strokeWidth={1.5} /></>)}
                </GoldButton>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </EnquiryContext.Provider>
  );
};
